import NavigationItemsSlider from './NavigationItemsSlider.jsx';
import {useEffect, useRef, useState} from 'react';
import HamburgerMenu from '../../HamburgerMenu.jsx';
import {Icon, Typography} from '@bonprix/design-system';
import NavigationTeaserImage from './NavigationTeaserImage.jsx';
import Landmark from '@core/scripts/components/landmark-adapter';
import {useCheckItemActive} from '../../hooks/use-check-item-active.js';
import ThirdLevelGrid from '@header/scripts/components/navigation/navigation-small/ThirdLevelGrid.jsx';

export default function NavigationSecondLayer({
    backToFirstLayer,
    firstLevelTitle,
    firstLevelId,
    secondLevelItems,
    selectSecondLevelId,
    visible,
    texts,
    secondLayerRef,
}) {
    const [activeSecondLevelId, setActiveSecondLevelId] = useState(selectSecondLevelId);
    const [nextActiveSecondLevelId, setNextActiveSecondLevelId] = useState(null);
    const imageWrapperRef = useRef(null);
    const navigationBackRef = useRef(null);
    const navigationSlider = useRef(null);

    useEffect(() => {
        setActiveSecondLevelId(selectSecondLevelId);
    }, [selectSecondLevelId]);

    useEffect(() => {
        scrollUpAfterSecondLevelIndexChanged();
    }, [activeSecondLevelId]);

    useEffect(() => {
        if (visible) {
            const item = secondLevelItems.find(item => item.id === activeSecondLevelId);
            if (item) {
                const lmValue = {view: `pictureNaviLayer\\${item.id}`};
                Landmark.sendActionLandmark(lmValue);
            }
        }
    }, [visible]);

    const activeSecondLevelIndex = secondLevelItems.findIndex(item => item.id === activeSecondLevelId);
    const nextActiveSecondLevelIndex = nextActiveSecondLevelId ? secondLevelItems.findIndex(item => item.id === nextActiveSecondLevelId) : null;

    const previousSlide = nextActiveSecondLevelIndex != null && nextActiveSecondLevelIndex < activeSecondLevelIndex;
    const nextSlide = nextActiveSecondLevelIndex != null && nextActiveSecondLevelIndex > activeSecondLevelIndex;

    const transition = () => {
        if (previousSlide) {
            return 'swipe-left';
        }
        if (nextSlide) {
            return 'swipe-right';
        }
        return '';
    };

    const secondLevelItem = secondLevelItems[activeSecondLevelIndex];

    let thirdLevelItems;
    let nextThirdLevelItems;
    if (activeSecondLevelIndex != null) {
        thirdLevelItems = secondLevelItem.subNavigationItems;
    }
    if (nextActiveSecondLevelIndex != null) {
        nextThirdLevelItems = secondLevelItems[nextActiveSecondLevelIndex].subNavigationItems;
    }

    const handleTeaserImageClick = () => {
        const naviLmValue = `pn\\${secondLevelItem.id}\\all\\`;
        Landmark.sendUserInterfaceLandmark({navi: naviLmValue});
    };

    const handleBackButtonClick = () => {
        const naviLmValue = `hn\\${firstLevelId}\\close\\`;
        Landmark.sendUserInterfaceLandmark({navi: naviLmValue});
        backToFirstLayer();
    };

    const handleThirdLevelItemClick = (index, shopId) => {
        const naviLmValue = `hn\\${shopId}${index === 0 ? '\\all\\' : '\\\\'}`;
        Landmark.sendUserInterfaceLandmark({navi: naviLmValue});
    };

    function renderItems(items, currentSecondLevelItem) {
        return (
            <div className="navigation__third-level">
                {items.length > 0 ? renderItemLinks(items) : renderDefaultLink(currentSecondLevelItem)}
            </div>
        );
    }

    function renderItemLinks(items) {
        return items.map((item, index) => <ItemLink key={index} item={item} index={index} />);
    }

    function renderDefaultLink(currentItem) {
        return <ItemLink item={currentItem} index={0} isDefault={true} />;
    }

    function ItemLink({item, index, isDefault = false}) {
        const handleClick = () => handleThirdLevelItemClick(index, item.id);

        const isItemActive = useCheckItemActive();
        const titleColor = item.fontColor || 'primary';

        return (
            <a
                href={item.url}
                onClick={handleClick}
                className={`navigation__third-level-item${isItemActive(item) ? ' -active' : ''}`}
            >
                <Typography color={titleColor} variant="h5" component={'span'}>
                    {isDefault ? texts['overview_text_label'] : item.title}
                </Typography>
            </a>
        );
    }

    return (
        <>
            <div className="navigation__second-layer-header-wrapper" ref={imageWrapperRef}>
                <a href={secondLevelItem.url} onClick={handleTeaserImageClick}>
                    <NavigationTeaserImage
                        key={`teaserImage_${activeSecondLevelIndex}`}
                        teaserImageSource={secondLevelItem.teaserImageSource}
                        title={secondLevelItem.title}
                        textVariant="bonprix-h4"
                    />
                </a>
                <HamburgerMenu className="navigation__second-layer-closer" />
            </div>

            <div className={'navigation__back'} onClick={handleBackButtonClick} ref={navigationBackRef}>
                <div className="navigation__back-icon">
                    <Icon variant="arrow-left" />
                </div>
                <Typography color="primary" component="div" variant="body1">
                    {firstLevelTitle}
                </Typography>
            </div>
            <NavigationItemsSlider
                items={secondLevelItems}
                activeId={activeSecondLevelId}
                setActiveId={setActiveSecondLevelId}
                setNextActiveId={setNextActiveSecondLevelId}
                ref={navigationSlider}
                texts={texts}
            />

            <div className="navigation__third-level-wrapper">
                {previousSlide && (
                    <div className={`navigation__sub-navigation-slide swipe-left`}>
                       <ThirdLevelGrid items={nextThirdLevelItems} currentSecondLevelItem={secondLevelItem} texts={texts} ></ThirdLevelGrid>
                    </div>
                )}

                <div className={`navigation__sub-navigation-slide ${transition()}`}>
                    <ThirdLevelGrid items={thirdLevelItems} currentSecondLevelItem={secondLevelItem} texts={texts} ></ThirdLevelGrid>
                </div>

                {nextSlide && (
                    <div className={`navigation__sub-navigation-slide swipe-right`}>
                        <ThirdLevelGrid items={nextThirdLevelItems} currentSecondLevelItem={secondLevelItem} texts={texts} ></ThirdLevelGrid>

                    </div>
                )}
            </div>
        </>
    );
    function scrollUpAfterSecondLevelIndexChanged() {
        if (secondLayerRef.current && imageWrapperRef.current && navigationBackRef.current) {
            const imageWrapperHeight = imageWrapperRef.current.getBoundingClientRect().height;
            const navigationBackHeight = navigationBackRef.current.getBoundingClientRect().height;
            const stickyTop = navigationSlider.current.getBoundingClientRect().height;
            const offset = imageWrapperHeight + navigationBackHeight + stickyTop;
            if (secondLayerRef.current.scrollTop > offset) {
                secondLayerRef.current.scrollTo({
                    top: offset,
                    behavior: 'smooth',
                });
            }
        }
    }
}
